import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { getModalTreeSearch, getTreeSearch, isConceptualSearchTurnedOn as getCSTurnOn } from "../../reducers/search";
import {
    toggleConceptual,
    toggleModalConceptual,
    treeSearchUserTyped,
    clearTreeSearch,
    clearModalTreeSearch,
} from "../../actions/searchActions";
import { searchColumns, requestScriptObjects } from "../../actions/fieldTreeActions";

import CircularLoading from "../loading/CircularLoading";
import Tooltip from "@material-ui/core/Tooltip";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
let hasRun = false;

const TreeSearch = ({ isActivityReports, isModal, isFieldExplorer }) => {
    const dispatch = useDispatch();

    const [searchBoxValue, setSearchBoxValue] = useState("");

    const initialTreeSearch = useSelector(state => (isModal ? state.search.modalTreeSearch : state.search.treeSearch)); // Just using this for when rehydrating / first load
    const isConceptualSearchTurnedOn = useSelector(state => getCSTurnOn(state, isModal));
    const conceptualPreference = useSelector(state => state.session.userSettings["conceptual-search-default"]);
    const wordvecServer = useSelector(
        state =>
            Array.isArray(state.session.enabledFeatures) &&
            state.session.enabledFeatures.includes("wordvec-server-filtering")
    );
    const treeSearch = useSelector(state => (isModal ? getModalTreeSearch(state) : getTreeSearch(state)));
    const willClearTree = useSelector(state =>
        isModal ? state.search.modalWillClearTree : state.search.willClearTree
    );
    const wordVecIsSearching = useSelector(state =>
        isModal ? state.search.modalWordVecIsSearching : state.search.wordVecIsSearching
    );
    const wordVecHasError = useSelector(state => state.search.wordVecHasError);
    const pathname = useSelector(state => state.router && state.router.location && state.router.location.pathname);
    const isFirstRun = useRef(true);

    useEffect(() => {
        if (isFirstRun.current) {
            initialTreeSearch && setSearchBoxValue(initialTreeSearch);
            isFirstRun.current = false;
        }
    }, [initialTreeSearch, setSearchBoxValue]);

    useEffect(() => {
        if (!hasRun) {
            hasRun = true;
            if (conceptualPreference) {
                const preferenceBool = conceptualPreference == "Y";
                if (preferenceBool != isConceptualSearchTurnedOn) {
                    dispatch(isModal ? toggleModalConceptual() : toggleConceptual());
                }
            }
        }
    }, [dispatch, conceptualPreference]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        //To remove the value from the tree search and avoid getting wrong highlithed results #6529
        if (isModal) {
            dispatch(treeSearchUserTyped("", false, isConceptualSearchTurnedOn, wordvecServer));
            dispatch(clearTreeSearch());
        }
    }, [isModal]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (willClearTree && treeSearch) {
            cancel();
        }
    }, [willClearTree]); // eslint-disable-line react-hooks/exhaustive-deps

    const onChange = e => {
        const value = e.target.value;
        setSearchBoxValue(value);
        // Clearing the field resets the tree, but searching only happens on demand (button click / Enter key)
        if (value.length === 0) {
            if (isFieldExplorer) {
                dispatch(requestScriptObjects());
            } else {
                dispatch(isModal ? clearModalTreeSearch() : clearTreeSearch());
                dispatch(treeSearchUserTyped("", isModal, isConceptualSearchTurnedOn, wordvecServer));
            }
        }
    };

    const triggerSearch = () => {
        const value = searchBoxValue;
        if (isFieldExplorer) {
            if (value.length > 2) {
                dispatch(searchColumns(value)).then(() => {
                    dispatch(treeSearchUserTyped(value, isModal, isConceptualSearchTurnedOn, wordvecServer));
                });
            } else if (value.length === 0) {
                dispatch(requestScriptObjects());
            }
        } else if (value.length == 0) {
            dispatch(isModal ? clearModalTreeSearch() : clearTreeSearch());
            dispatch(treeSearchUserTyped("", isModal, isConceptualSearchTurnedOn, wordvecServer));
        } else {
            dispatch(treeSearchUserTyped(value, isModal, isConceptualSearchTurnedOn, wordvecServer));
        }
    };

    const onKeyDown = e => {
        if (e.key === "Enter") {
            e.preventDefault();
            triggerSearch();
        }
    };

    const cancel = () => {
        setSearchBoxValue("");
        dispatch(treeSearchUserTyped("", isModal, isConceptualSearchTurnedOn, wordvecServer));

        if (isFieldExplorer) {
            dispatch(requestScriptObjects()).then(() => {
                dispatch(isModal ? clearModalTreeSearch() : clearTreeSearch());
            });
        } else {
            dispatch(isModal ? clearModalTreeSearch() : clearTreeSearch());
        }
    };

    const clickConceptualIcon = () => dispatch(isModal ? toggleModalConceptual() : toggleConceptual());

    const getConceptualToolTip = () => {
        // Not perfect, especially when we add double word support
        const conceptualFoundMatches = Array.isArray(treeSearch) && treeSearch.length > 1;
        let conceptualSearchTooltip;
        if (isConceptualSearchTurnedOn && !searchBoxValue) {
            conceptualSearchTooltip = "Conceptual search active.";
        } else if (isConceptualSearchTurnedOn && wordVecIsSearching) {
            conceptualSearchTooltip = "Conceptual search active. Searching...";
        } else if (isConceptualSearchTurnedOn && wordVecHasError) {
            conceptualSearchTooltip = "Could not contact conceptual search service.";
        } else if (isConceptualSearchTurnedOn && !conceptualFoundMatches) {
            conceptualSearchTooltip = `Conceptual search active. No conceptual match terms found for "${searchBoxValue}"`;
        } else if (isConceptualSearchTurnedOn && conceptualFoundMatches) {
            conceptualSearchTooltip = `Conceptual search active. (${
                treeSearch.length - 1
            }) conceptual match terms found for "${searchBoxValue}".`;
            /*
            // will display the various search matches
            searchValues = treeSearch.map((word, i) => (
                <span key={word + "outer"}>
                    <span key={word} className="label label-default" style={{ fontWeight: 400 }}>
                        {word.replace(/"$/, "").replace(/^"/, "")}
                    </span>{" "}
                    {i % 5 == 0 ? <br /> : null}
                </span>
            ));

            conceptualSearchTooltip = (
                <div>
                    <span>
                        Conceptual search active. Searched for words related to:
                        <br />
                        {searchValues}
                    </span>
                </div>
            );
            */
        } else {
            conceptualSearchTooltip = "Conceptual search disabled.";
        }

        return conceptualSearchTooltip;
    };

    const isInFlows = pathname && pathname.includes("flows");
    const isFlowTree = pathname && pathname.includes("design");
    const isInSchedules = pathname && pathname.includes("schedules");

    return (
        <div style={{ display: "flex", alignItems: "center" }}>
            <InputBase
                key="tree-search"
                style={{ padding: 0 }}
                className={
                    isInFlows ? "form-control tree-search-input material-ui-input" : "form-control tree-search-input"
                }
                placeholder={
                    isFlowTree ? "Search..." : isInSchedules ? "Search by Keyword or FlowId" : "Search by Keyword"
                }
                fullWidth
                onChange={e => onChange(e)}
                onKeyDown={onKeyDown}
                margin="none"
                value={searchBoxValue}
                startAdornment={
                    !isActivityReports ? (
                        <InputAdornment position="end">
                            <Tooltip title={getConceptualToolTip()}>
                                <IconButton
                                    onClick={e => {
                                        e.stopPropagation(); // Stop event propagation
                                        clickConceptualIcon();
                                    }}
                                    className={
                                        isConceptualSearchTurnedOn
                                            ? "conceptual-icon-button active"
                                            : "conceptual-icon-button"
                                    }
                                >
                                    <i className="conceptual-icon" />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ) : (
                        <div style={{ width: "5px" }} /> //padding in flow tree and activity reports
                    )
                }
                endAdornment={
                    wordVecIsSearching ? (
                        <CircularLoading size="x-small" color="#575755" />
                    ) : searchBoxValue ? (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={e => {
                                    e.stopPropagation(); // Stop event propagation
                                    cancel();
                                }}
                            >
                                <i className="material-icons">clear</i>
                            </IconButton>
                        </InputAdornment>
                    ) : null
                }
            />
            <Tooltip title="Search by keyword">
                <IconButton size="small" className="tree-search-button" onClick={triggerSearch}>
                    <i className="material-icons">search</i>
                </IconButton>
            </Tooltip>
        </div>
    );
};

TreeSearch.propTypes = {
    // all injected by redux
    initialTreeSearch: PropTypes.string,
    clearTreeSearch: PropTypes.func,
    clearModalTreeSearch: PropTypes.func,
    treeSearchUserTyped: PropTypes.func,
    toggleConceptual: PropTypes.func,
    toggleModalConceptual: PropTypes.func,
    treeSearch: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    willClearTree: PropTypes.bool,
    isConceptualSearchTurnedOn: PropTypes.bool,
    wordVecIsSearching: PropTypes.bool,
    wordVecHasError: PropTypes.bool,
    conceptualPreference: PropTypes.string,
    pathname: PropTypes.string,
    //passed in
    isActivityReports: PropTypes.bool,
    isModal: PropTypes.bool,
    isFieldExplorer: PropTypes.bool,
};

export default TreeSearch;
